/**
 * Created by andresvasquez on 12/14/17.
 */
import {Injectable} from '@angular/core';
import {BooleanUDF} from "../models/UDFs/BooleanUDF";
import {StringUDF} from "../models/UDFs/StringUDF";
import {NumberUDF} from "../models/UDFs/NumberUDF";
import {AppParametersConfiguration} from "../models/appParameters.config";
import {ChangeStatusConfiguration} from "../models/changeStatus.config";
import {READER_TYPE_BARCODE} from "../app/app.constants";
import {CycleCountConfiguration} from "../models/cycleCount.config";
import {ConfigurationThing} from "../models/configurationThing";
import {isUndefined} from "util";
import {TranslateService} from "@ngx-translate/core";
import {FindItemConfiguration} from "../models/findItem.config";
import {UnassociatedConfiguration} from "../models/unassociated.config";
import {AssociationConfiguration} from "../models/association.config";
import {IdentifyItemConfiguration} from "../models/identifyItem.config";
import {SimulatorConfiguration} from "../models/simulator.config";
import {ReplenishmentConfiguration} from "../models/replenishment.config";
import {EncodeTagConfiguration} from "../models/encodeTag.config";


@Injectable()
export class ConfigurationService {

  private static tag = '[CONFIGURATION_SERVICE]';

  constructor(public translate: TranslateService) {
  }

  /**
   * Validate configuration thing
   * @param config
   * @returns {any}
   */
  public validateConfiguration(config: ConfigurationThing): string {
    if (isUndefined(config.AppParametersConfiguration) || config.AppParametersConfiguration == null) {
      return this.translate.instant('config.toasts.invalidConfiguration') + " AppParametersConfiguration";
    }

    if (isUndefined(config.EncodeTagConfiguration) || config.EncodeTagConfiguration == null) {
      return this.translate.instant('config.toasts.invalidConfiguration') + " EncodeTagConfiguration";

    }

    if (isUndefined(config.AssociationConfiguration) || config.AssociationConfiguration == null) {
      return this.translate.instant('config.toasts.invalidConfiguration') + " AssociationConfiguration";

    }
    if (isUndefined(config.ChangeStatusConfiguration) || config.ChangeStatusConfiguration == null) {
      return this.translate.instant('config.toasts.invalidConfiguration') + " ChangeStatusConfiguration";

    }
    if (isUndefined(config.CycleCountConfiguration) || config.CycleCountConfiguration == null) {
      return this.translate.instant('config.toasts.invalidConfiguration') + " CycleCountConfiguration";

    }

    if (isUndefined(config.FindItemConfiguration) || config.FindItemConfiguration == null) {
      return this.translate.instant('config.toasts.invalidConfiguration') + " FindItemConfiguration";

    }
    if (isUndefined(config.IdentifyItemConfiguration) || config.IdentifyItemConfiguration == null) {
      return this.translate.instant('config.toasts.invalidConfiguration') + " IdentifyItemConfiguration";

    }
    if (isUndefined(config.ReaderConfiguration) || config.ReaderConfiguration == null) {
      return this.translate.instant('config.toasts.invalidConfiguration') + " ReaderConfiguration";

    }
    if (isUndefined(config.UnassociatedConfiguration) || config.UnassociatedConfiguration == null) {
      return this.translate.instant('config.toasts.invalidConfiguration') + " UnassociatedConfiguration";

    }
    if (isUndefined(config.ReplenishmentConfiguration) || config.ReplenishmentConfiguration == null) {
      return this.translate.instant('config.toasts.invalidConfiguration') + " ReplenishmentConfiguration";

    }


    //Validate child recursive
    let validateAppParameters = this.validateAppParametersConfiguration(config.AppParametersConfiguration);
    if (validateAppParameters.length > 0) {
      return this.translate.instant('config.toasts.invalidConfiguration') + " " + validateAppParameters[0];
    }

    let validateAssociation = this.validateAssociationConfiguration(config.AssociationConfiguration);
    if (validateAssociation.length > 0) {
      return this.translate.instant('config.toasts.invalidConfiguration') + " " + validateAssociation[0];
    }


    let validateChangeStatus = this.validateChangeStatusConfiguration(config.ChangeStatusConfiguration);
    if (validateChangeStatus.length > 0) {
      return this.translate.instant('config.toasts.invalidConfiguration') + " " + validateChangeStatus[0];
    }

    let validateIdentifyItem = this.validateIdentityItemConfiguration(config.IdentifyItemConfiguration);
    if (validateIdentifyItem.length > 0) {
      return this.translate.instant('config.toasts.invalidConfiguration') + " " + validateIdentifyItem[0];
    }

    let validateCycleCount = this.validateCycleCountConfiguration(config.CycleCountConfiguration);
    if (validateCycleCount.length > 0) {
      return this.translate.instant('config.toasts.invalidConfiguration') + " " + validateCycleCount[0];
    }


    let validateEncodeTag = this.validateEncodeTagConfiguration(config.EncodeTagConfiguration);
    if (validateEncodeTag.length > 0) {
      return this.translate.instant('config.toasts.invalidConfiguration') + " " + validateEncodeTag[0];
    }

    let validateFindItem = this.validateFindItemConfiguration(config.FindItemConfiguration);
    if (validateFindItem.length > 0) {
      return this.translate.instant('config.toasts.invalidConfiguration') + " " + validateFindItem[0];
    }

    let validateUnassociated = ConfigurationService.validateUnassociatedConfiguration(config.UnassociatedConfiguration);
    if (validateUnassociated.length > 0) {
      return this.translate.instant('config.toasts.invalidConfiguration') + " " + validateUnassociated[0];
    }

    let validateReplenishment = ConfigurationService.validateReplenishmentConfiguration(config.ReplenishmentConfiguration);
    if (validateReplenishment.length > 0) {
      return this.translate.instant('config.toasts.invalidConfiguration') + " " + validateReplenishment[0];
    }

    return "";
  }


  /* **********************************************************************************************************
   *                        App Parameters
   * **********************************************************************************************************
   */

  /**
   * Get Default app parameters
   * @returns {AppParametersConfiguration}
   */
  private getDefaultAppParametersConfiguration(): AppParametersConfiguration {
    let obj = new AppParametersConfiguration();
    obj.AllDepartmentEnabled = new BooleanUDF(true);
    obj.AutoPadRFID = new BooleanUDF(false);
    obj.AutoPadSKU = new BooleanUDF(true);
    obj.AutoPadUPC = new BooleanUDF(true);
    obj.EnableTimestamp = new BooleanUDF(false);
    obj.RequestTimeout = new NumberUDF(30);
    obj.RFIDLength = new StringUDF("24");
    obj.RFIDValidation = new StringUDF("^[A-F0-9]{24}$");
    obj.SKULength = new StringUDF("8");
    obj.SKUValidation = new StringUDF("^[0-9]{8}$");
    obj.UPCLength = new StringUDF("13");
    obj.UPCValidation = new StringUDF("^[0-9]{13}$");
    obj.VisibilityGroupId = new StringUDF("4");
    obj.ViZIxEventSerial = new StringUDF("VIZIXEVENTS");
    obj.ItemEventUDF = new StringUDF("Item_Event");
    obj.DisableUPCCheck = new BooleanUDF(false);
    obj.OfflineMode = new BooleanUDF(false);
    return obj;
  }


  private validateAppParametersConfiguration(config: AppParametersConfiguration): Array<any> {
    return ConfigurationService.isNullOrEmpty(config, [
      "DefaultDepartment",
      "DefaultZone",
      "Group",
      "FilterLabelStoreId",
      "FilterLabelZoneTypeProperty",
      "ViZIxEventSerial",
      "ZoneReportId",
      "ThingTypeCodeItem",
      "ThingTypeCodeProduct"

    ]);
  }


  /* **********************************************************************************************************
   *                        Association
   * **********************************************************************************************************
   */

  /**
   * Get Default Association Configuration
   * @returns {AssociationConfiguration}
   */
  private getDefaultAssociationConfiguration(selectedConfiguration: ConfigurationThing): AssociationConfiguration {
    let obj = new AssociationConfiguration();
    obj.AllowAssociationOverride = new BooleanUDF(true);
    obj.AllowDefaultProductInsert = new BooleanUDF(false);
    obj.AllowKeyboardInputForAssociations = new BooleanUDF(true);
    obj.AutoSave = new BooleanUDF(true);
    obj.AutoSaveTimer = new NumberUDF(1000);

    obj.FirstFieldLabel = new StringUDF("UPC");
    obj.FirstFieldReaderType = new StringUDF(READER_TYPE_BARCODE);
    obj.FirstFieldValidation = new StringUDF("^[0-9]{13}$");

    obj.SecondFieldLabel = new StringUDF("EPC");
    obj.SecondFieldReaderType = new StringUDF(READER_TYPE_BARCODE);
    obj.SecondFieldValidation = new StringUDF("^[0-9A-F]{24}$");
    obj.ShowZonePickList = new BooleanUDF(true);
    obj.DefaultAssociationZone = new StringUDF(selectedConfiguration.AppParametersConfiguration.DefaultZone.value);
    return obj;
  }

  private validateAssociationConfiguration(config: AssociationConfiguration): Array<any> {
    return ConfigurationService.isNullOrEmpty(config, [
      "AssociationZoneUDF",
      "FirstBarcodeUDF",
      "FirstThingTypeCode",
      "SecondBarcodeUDF",
      "SecondThingTypeCode",
      "DefaultProductSKUUDF",
      "DefaultProductUPCUDF",
      "ThingTypeCodeToSync",
      "ItemUPCUDF"
    ]);
  }

  /* **********************************************************************************************************
  *                        Encode Tags
  * **********************************************************************************************************
  */

  /**
   * Get Default EncodeTags Configuration
   * @returns {EncodeTagsConfiguration}
   */
  private getDefaultEncodeTagConfiguration(): EncodeTagConfiguration {
    let obj = new EncodeTagConfiguration();
    obj.AllowKeyboardInputForAssociations = new BooleanUDF(true);
    obj.AutoSave = new BooleanUDF(false);
    obj.AutoSaveTimer = new NumberUDF(0);
    obj.CompanyPrefixLenght = new NumberUDF(6);
    obj.DefaultEncodeZone = new StringUDF("Stockroom");
    obj.DefaultProductUPCUDF = new StringUDF("serialNumber");
    obj.EncodeFilterValue = new StringUDF("1");
    obj.EncodeZoneUDF = new StringUDF("zone");
    obj.FirstBarcodeUDF = new StringUDF("UPCNumber");
    obj.FirstFieldAutoPad = new NumberUDF(0);
    obj.FirstFieldLabel = new StringUDF("UPC");
    obj.FirstFieldReaderType = new StringUDF(READER_TYPE_BARCODE);
    obj.FirstFieldValidation = new StringUDF("^[0-9]{14}$");
    obj.FirstThingTypeCode = new StringUDF("Products");
    obj.HeaderValue = new StringUDF("48");
    obj.ItemUPCUDF = new StringUDF("K_UPCNumber");
    obj.OriginalValueUDF = new StringUDF("OriginalEPC");
    obj.Partition = new StringUDF("5");
    obj.SecondBarcodeUDF = new StringUDF("serialNumber");
    obj.SecondFieldAutoPad = new NumberUDF(14);
    obj.SecondFieldLabel = new StringUDF("RFID");
    obj.SecondFieldReaderType = new StringUDF(READER_TYPE_BARCODE);
    obj.SecondFieldValidation = new StringUDF("^[0-9A-F]{24}$");
    obj.SecondThingTypeCode = new StringUDF("Item");
    obj.ShowZonePickList = new BooleanUDF(true);
    obj.ThingTypeCodeToSync = new StringUDF("Item");
    obj.UseTID = new BooleanUDF(false);
    obj.ValueExpression = new StringUDF("");
    obj.WritePassword = new StringUDF("");
    return obj;
  }

  /**
   * Validate EncodeTags configuration thing. Use this function to define mandatory params
   * @param config configuration object
   * @returns {Array<any>} things null or empty
   */
  private validateEncodeTagConfiguration(config: EncodeTagConfiguration): Array<any> {
    let dinamicEncodeProperties = ["EncodeSchema", "EncodeSerialThingTypeCode"];
    let isSecondEncodeSchema: boolean = false;
    let isEncodeSerialThingTypeCodeNullOrEmpty: boolean = false;
    for (let propertyName of dinamicEncodeProperties) {
      if (config.hasOwnProperty(propertyName)) {
        let property = config[propertyName];
        if (propertyName.endsWith("EncodeSchema")) {
          if (property.value === "2") {
            isSecondEncodeSchema = true;
          }
        }
        else if (propertyName.endsWith("EncodeSerialThingTypeCode")) {
          if (property.value === "" || property.value === null || isUndefined(property.value)) {
            isEncodeSerialThingTypeCodeNullOrEmpty = true;
          }
        }
      }
    }

    let lstErrors = ConfigurationService.isNullOrEmpty(config, [
      "DefaultProductUPCUDF",
      "EncodeZoneUDF",
      "FirstBarcodeUDF",
      "FirstThingTypeCode",
      "SecondBarcodeUDF",
      "SecondThingTypeCode",
      "ThingTypeCodeToSync",
      "ItemUPCUDF",
      "FirstFieldAutoPad",
      "SecondFieldAutoPad",
      "HeaderValue",
      "EncodeFilterValue",
      "CompanyPrefixLenght",
      "Partition",
      "ValueExpression"
    ]);

    if (isSecondEncodeSchema && isEncodeSerialThingTypeCodeNullOrEmpty) {
      lstErrors.push("EncodeSerialThingTypeCode");
    }

    return lstErrors;
  }


  /* **********************************************************************************************************
   *                        Change Status
   * **********************************************************************************************************
   */

  /**
   * Get Default change status config
   * @returns {ChangeStatusConfiguration}
   */
  private getDefaultChangeStatusConfiguration(): ChangeStatusConfiguration {
    let obj = new ChangeStatusConfiguration();
    obj.FieldReaderType = new StringUDF(READER_TYPE_BARCODE);
    obj.FieldValidation = new StringUDF("^[0-9A-F]{24}$");
    return obj;
  }


  private validateChangeStatusConfiguration(config: ChangeStatusConfiguration): Array<any> {
    return ConfigurationService.isNullOrEmpty(config, [
      "ProductStatusLabelReport",
      "ProductStatusThingTypeCode",
      "ChangeStatusReportId"
    ]);
  }


  /* **********************************************************************************************************
   *                        Cycle Count
   * **********************************************************************************************************
   */

  /**
   * Get Default Cycle Count configuration thing
   * @returns {CycleCountConfiguration}
   */
  private getDefaultCycleCountConfiguration(): CycleCountConfiguration {
    let obj = new CycleCountConfiguration();
    obj.BeepOnUniqueTags = new BooleanUDF(true);
    obj.CycleCountScreenType = new StringUDF("Summary");
    obj.PowerLevel = new NumberUDF(100);
    obj.SyncInterval = new NumberUDF(200);
    obj.EndPointMode = new StringUDF('things'); //thingBridge
    obj.RetrySyncAttempts = new NumberUDF(5); //thingBridge
    obj.RetrySyncButton = new BooleanUDF(true); //thingBridge
    obj.RetrySyncInterval = new NumberUDF(120); //thingBridge
    obj.UpdateRemainingDelay = new NumberUDF(30);
    return obj;
  }

  private validateCycleCountConfiguration(config: CycleCountConfiguration): Array<any> {
    return ConfigurationService.isNullOrEmpty(config, [
      "ExpectedReportId",
      "LastCycleCountReportId",
      "ThingTypeCodeSync",
      "FilterLabelZoneType",
      "FilterLabelStoreID",
      "FilterLabelDepartment"
    ]);
  }

  /* **********************************************************************************************************
   *                        Identify Item
   * **********************************************************************************************************
   */

  /**
   * Get Default Itentify Configuration
   * @returns {IdentifyItemConfiguration}
   */
  private getDefaultIdentifyItemConfiguration(): IdentifyItemConfiguration {
    let obj = new IdentifyItemConfiguration();
    obj.DefaultMode = new StringUDF(READER_TYPE_BARCODE);
    return obj;
  }

  private validateIdentityItemConfiguration(config: IdentifyItemConfiguration): Array<any> {
    return ConfigurationService.isNullOrEmpty(config, [
      "FieldValueSOH",
      "FilterLabelRFID",
      "FilterLabelSKU",
      "FilterLabelSOHSerial",
      "FilterLabelSOHStoreID",
      "FilterLabelUPC",
      "IdentifyProductReportId",
      "IdentifyItemReportId",
      "SOHReportId"
    ]);
  }

  /* **********************************************************************************************************
   *                        Find Item
   * **********************************************************************************************************
   */

  /**
   * Get Default Find Items configuration
   * @returns {FindItemConfiguration}
   */
  private getDefaultFindItemConfiguration(): FindItemConfiguration {
    let obj = new FindItemConfiguration();
    obj.DefaultPowerLevelReader = new NumberUDF(100);
    obj.ProximityAcceptanceCriteriaReader = new NumberUDF(80);
    obj.EnableZoneType = new BooleanUDF(true);
    return obj;
  }

  private validateFindItemConfiguration(config: FindItemConfiguration): Array<any> {
    return ConfigurationService.isNullOrEmpty(config, [
      "FindItemExpectedReportId"
    ]);
  }

  /* **********************************************************************************************************
   *                        Unasscoiated Tags
   * **********************************************************************************************************
   */

  /**
   * Get Default Unassociated Configuration
   * @returns {UnassociatedConfiguration}
   */
  private getDefaultUnassociatedConfiguration(): UnassociatedConfiguration {
    let obj = new UnassociatedConfiguration();
    obj.DefaultPowerLevel = new NumberUDF(100);
    obj.ProximityAcceptanceCriteria = new NumberUDF(80);
    return obj;
  }

  /**
   * Validate unassocited configuration thing. Use this function to define mandatory params
   * @param config configuration object
   * @returns {Array<any>} things null or empty
   */
  private static validateUnassociatedConfiguration(config: UnassociatedConfiguration): Array<any> {
    return ConfigurationService.isNullOrEmpty(config, [
      "ReportUnassociatedId",
      "FilterLabelZoneType",
      "FilterLabelStoreID"
    ]);
  }

  /* **********************************************************************************************************
   *                        Replenishment
   * **********************************************************************************************************
   */

  /**
   * Get Default Replenishment Configuration
   * @returns {ReplenishmentConfiguration}
   */
  private getDefaultReplenishmentConfiguration(): ReplenishmentConfiguration {
    let obj = new ReplenishmentConfiguration();
    //all is mandatory
    return obj;
  }

  /**
   * Validate Replenishment configuration thing. Use this function to define mandatory params
   * @param config configuration object
   * @returns {Array<any>} things null or empty
   */
  private static validateReplenishmentConfiguration(config: ReplenishmentConfiguration): Array<any> {
    return ConfigurationService.isNullOrEmpty(config, [
      "ReplenishmentReportId",
      "SearchPosition",
      "StoreIDFilterLabel"
    ]);
  }


  /* **********************************************************************************************************
   *                        Simulator Config
   * **********************************************************************************************************
   */

  /**
   * Get defatult simulator configuration
   * @returns {SimulatorConfiguration}
   */
  private static getDefaultSimulatorConfiguration(): SimulatorConfiguration {
    let obj = new SimulatorConfiguration();
    obj.ButtonControl = new BooleanUDF(true);
    obj.Enabled = new BooleanUDF(true);
    obj.InitialDelay = new NumberUDF(3000);
    obj.Interval = new NumberUDF(50);
    obj.IsFromJsonFile = new BooleanUDF(false);
    obj.Quantity = new NumberUDF(10000);
    obj.IsRssiIncremental = new BooleanUDF(true);
    obj.RssiChangeInterval = new NumberUDF(20);
    return obj;
  }


  /**
   * Verify mandatory child
   * @param config Configuration thing
   * @param lstRequiredProperties Array with names of mandatory properties
   * @returns {Array} of strings with empty properties
   */
  private static isNullOrEmpty(config: any, lstRequiredProperties: Array<string>): Array<any> {
    let lstErrors = [];
    for (let propertyName of lstRequiredProperties) {
      if (config.hasOwnProperty(propertyName)) {
        let property = config[propertyName];

        if (typeof property.value == "string") {
          if (property.value == null || property.value == "") {
            lstErrors.push(propertyName);
          } else {
            if (propertyName.endsWith("ReportId") && property.value.trim() == "0") {
              lstErrors.push(propertyName);
            }
          }
        } else if (typeof property.value == "boolean") {
          if (property.value == null) {
            lstErrors.push(propertyName);
          }
        } else if (typeof property.value == "number") {
          /*if (property.value == null || <number> property.value == 0) {
            lstErrors.push(propertyName);
          }*/
          if (propertyName.endsWith("SearchPosition")) {
            if (property.value == null || property.value <= 0 || property.value > 6) {
              lstErrors.push(propertyName);
            }
            else {
              if (property.value % 1 != 0) {
                lstErrors.push(propertyName);
              }
            }

          }
          else {
            if (propertyName.endsWith("AutoPad")) {
              if (property.value == null || property.value < 0) {
                lstErrors.push(propertyName);
              }
            } else {
              if (property.value == null || <number> property.value == 0) {
                lstErrors.push(propertyName);
              }
            }
          }

        } else if (typeof property.value == "undefined") {
          lstErrors.push(propertyName);
        }
      } else {
        lstErrors.push(propertyName);
      }
    }
    return lstErrors;
  }


  /**
   * Fill configuration thing with default values
   * @param selectedConfig thing from ViZix
   */
  fillDefaultValues(selectedConfig: ConfigurationThing) {
    let baseConfigThing = new ConfigurationThing();
    baseConfigThing.AppParametersConfiguration = this.getDefaultAppParametersConfiguration();
    baseConfigThing.AssociationConfiguration = this.getDefaultAssociationConfiguration(selectedConfig);
    baseConfigThing.ChangeStatusConfiguration = this.getDefaultChangeStatusConfiguration();
    baseConfigThing.CycleCountConfiguration = this.getDefaultCycleCountConfiguration();
    baseConfigThing.IdentifyItemConfiguration = this.getDefaultIdentifyItemConfiguration();
    baseConfigThing.EncodeTagConfiguration = this.getDefaultEncodeTagConfiguration();
    baseConfigThing.FindItemConfiguration = this.getDefaultFindItemConfiguration();
    baseConfigThing.UnassociatedConfiguration = this.getDefaultUnassociatedConfiguration();
    baseConfigThing.SimulatorConfiguration = ConfigurationService.getDefaultSimulatorConfiguration();
    baseConfigThing.ReplenishmentConfiguration = this.getDefaultReplenishmentConfiguration();

    ConfigurationService.fillRecursive(selectedConfig.AppParametersConfiguration, baseConfigThing.AppParametersConfiguration);
    ConfigurationService.fillRecursive(selectedConfig.AssociationConfiguration, baseConfigThing.AssociationConfiguration);
    ConfigurationService.fillRecursive(selectedConfig.ChangeStatusConfiguration, baseConfigThing.ChangeStatusConfiguration);
    ConfigurationService.fillRecursive(selectedConfig.CycleCountConfiguration, baseConfigThing.CycleCountConfiguration);
    ConfigurationService.fillRecursive(selectedConfig.IdentifyItemConfiguration, baseConfigThing.IdentifyItemConfiguration);
    ConfigurationService.fillRecursive(selectedConfig.FindItemConfiguration, baseConfigThing.FindItemConfiguration);
    ConfigurationService.fillRecursive(selectedConfig.EncodeTagConfiguration, baseConfigThing.EncodeTagConfiguration);
    ConfigurationService.fillRecursive(selectedConfig.UnassociatedConfiguration, baseConfigThing.UnassociatedConfiguration);
    ConfigurationService.fillRecursive(selectedConfig.ReplenishmentConfiguration, baseConfigThing.ReplenishmentConfiguration);

    if (selectedConfig.SimulatorConfiguration != null) {
      ConfigurationService.fillRecursive(selectedConfig.SimulatorConfiguration, baseConfigThing.SimulatorConfiguration);
    }
  }


  /**
   * Fill recursive configuration thing with default configuration thing
   * @param configObject => Thing to fill
   * @param baseConfig => Default thing
   */
  private static fillRecursive(configObject: any, baseConfig: any) {
    let defaultProperyNames = Object.getOwnPropertyNames(baseConfig);
    for (let propertyName of defaultProperyNames) {
      try {
        if (configObject != null) {
          if (!configObject.hasOwnProperty(propertyName)) {
            configObject[propertyName] = baseConfig[propertyName];
          }
        }
      } catch (e) {
        console.log(this.tag, 'Error in recursive property fill function', configObject);
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/providers/configuration.service.ts